import { computed, onMounted, reactive, ref } from 'vue';
import type { ValidFields, FormValidation, Form  } from '@/types/form';
import { allForms } from "@/config/messageForm";
import { useAppStore } from "../store/app";
import { fetchUserInfo } from './userService';
import type { UserInfoResponse } from '@/types/user';

declare global {
  interface Window {
    gazeta_pl?: {
      forumSettings?: {
        threadId?: string | number;
        forumId?: string | number;
      };
    };
    wyborcza_pl?: {
      userInfo?: {
        nick?: string;
      };
    };
  }
}

export function useFormLogic() {
  const isTestMode = import.meta.env.MODE === 'test';

  const isFormValidObject: FormValidation = reactive({
    reason: true,
    explain: true,
    userMail: true,
    statute: true,
  });

  const appStore = useAppStore();
  const reportCommentID = computed(() => appStore.getReportCommentID[0]);
  const service = appStore.getService;
  const showExplainWarning = ref(false);
  const isExplainIncorrect = ref(false);
  const isHelperRequired = ref(false);
  const isMailAdded = ref(false);
  const selectList = ref(false);
  const selectDataSet = ref('');
  const selectValue = ref('Wybierz powód zgłoszenia');
  const helperTextArea = ref('Maksymalna liczba znaków wynosi 500.');
  const userInfo = ref<UserInfoResponse | null>(null);
  const formInputs = reactive({
    reason: 'Wybierz powód zgłoszenia',
    explain: '',
    userEmail: '',
    userName: '',
    rules: false,
  });

  const isLocalhostDevUrl = (url: string) => {
    return url.includes('http://localhost:5173/');
  };

  const getFakeUserData = (): UserInfoResponse => {
    return {
      username: 'james_bond',
      name: 'James',
      surname: 'Bond',
      email: 'james.bond@example.com',
      loggedIn: true,
      ptoken: new Date().getTime().toString(),
    };
  };

  onMounted(async () => {
    const userData = isLocalhostDevUrl(window.location.href)
      ? getFakeUserData()
      : await fetchUserInfo(service);
    
    if (userData?.loggedIn) {
      if (isTestMode) {
        console.log('Dane Użytkownika: ', userData);
      }
      const login = window?.wyborcza_pl?.userInfo?.nick || userData?.username ||"";
      const name = userData?.name || "";
      const surname = userData?.surname || "";
      const nameToDisplay = (name + " " + surname).trim() || "";
      const userEmail = userData?.email || "";
      formInputs.userName = nameToDisplay;
      formInputs.userEmail = userEmail;
      appStore.setSingleFormField('userName', nameToDisplay);
      appStore.setSingleFormField('userEmail', userEmail || "");
      appStore.setSingleFormField('userLogin', login); 
      appStore.setSingleFormField('isLogged', !!userData.loggedIn);
      appStore.setSingleFormField('coreEmail', userEmail);

    }
    userInfo.value = userData as UserInfoResponse;
  });


  const changeSelect = (e: Event) => {
    const target = e.target as HTMLSelectElement; 
  
    if (target) {
      const value = target.dataset.value || ""; //"hate_speech"
      const name = target.textContent  || ""; //Mowa nienawiści

      selectDataSet.value = value;
      selectValue.value = name;
      formInputs.reason = name;

      showExplainWarning.value = value === "others";
      isExplainIncorrect.value = value === "others";
      isHelperRequired.value = value === "others";

      const input = document.querySelector<HTMLInputElement>(".dsa__reportSelectInput");
      input?.classList?.add('dsa__reportSelectInput--active');

      setTimeout(() => {
        checkField(input);
      }, 100)

    }
  };

  const updateFormData = (formData:any) => {
    const data = {
      source: service || "",
      commentId: reportCommentID.value,
      url: prepareUrl(window.location, reportCommentID.value, allForms[service]),
      reason: selectValue.value,
      explain: formData.explain,
      userEmail: formData.userEmail,
      isEmailAdded: isEmailValid(formData.userEmail) && formData.userEmail.length > 0,
      userName: formData.userName,
      rules: formData.rules ? "accepted" : "not_accepted",
      ptoken: userInfo.value?.ptoken || null,
      site_name: window.location.hostname || null, //www.plotek.pl 
      thread_id: (window as Window & { gazeta_pl?: { forumSettings?: { threadId?: string | number } } })?.gazeta_pl?.forumSettings?.threadId || 0, //threadId to id wątku na forum, domyślnie 0
      object_id: getID(service) //objectId to xx artykułu dla artykułów lub xx forum dla forum 
    };
    appStore.setFormData(data);

    return data;
  };

  const getID = (service: string) =>  {
    if(service === 'forum'){
      return (window as Window & { gazeta_pl?: { forumSettings?: { forumId?: string | number } } })?.gazeta_pl?.forumSettings?.forumId || null;
    } else{
      const url = window.location.href;
      // Szukam dopasowania /7,<liczba>,
      const match = url.match(/\/(5|7|12|14|56),(\d+),(\d+)/);
      if (match && match[3]) {
        return parseInt(match[3], 10);
      } else{
        return null;
      }
    }
  }

  const checkField = (input: any) => {
    if (input) {
      
      let isValid = true;
      const inputType: ValidFields = input.getAttribute("name") as ValidFields;
      const inputValue = input.value;

      
      if (inputType === "reason") {
        const selectValue = input.dataset.value;
        isValid = !!selectValue;
        
        showExplainWarning.value = selectValue === "others";
        isExplainIncorrect.value = selectValue === "others";
        isHelperRequired.value = selectValue === "others";
      }
      if (inputType === "explain") {

        const helper = checkTextArea(input);
        
        if (helper) {
          isHelperRequired.value = true;
          helperTextArea.value = helper;
        }

        const currentLength = inputValue?.length || 0;
        const isLenghthOk = currentLength >= 10 && currentLength <= 500;

        if (showExplainWarning.value) { 
          isValid = isLenghthOk; 
          isExplainIncorrect.value = !isLenghthOk; 
        } else { 
          if (currentLength === 0) { 
            isValid = true; 
            isExplainIncorrect.value = false; 
          } else { 
            isValid = isLenghthOk; 
            isExplainIncorrect.value = !isLenghthOk; 
          } 
        }

      }
      if (inputType === "userMail") {
        isValid = isEmailValid(inputValue);

        const userEmail = appStore.getFormData.userEmail;
        appStore.setSingleFormField('isUserChangeEmail', userEmail !== inputValue);

        if (isValid && inputValue.length > 0) {
          isMailAdded.value = true;
        }
      }
      if (inputType === "statute") {
        isValid = input.checked;
      }
      if (isValid) {
        input.classList.remove("dsa__reportError");

        if (inputType === "statute" && input.nextElementSibling) {
          input.nextElementSibling.classList.remove("dsa__reportError");
        }

        if (inputType === "reason") {
          input.parentElement.classList.remove("dsa__reportError");
        }

      } else {
        input.classList.add("dsa__reportError");
        if (inputType === "statute" && input.nextElementSibling) {
          input.nextElementSibling.classList.add("dsa__reportError");
        }
        if (inputType === "reason") {
          input.parentElement.classList.add("dsa__reportError");
        }
      }

      (isFormValidObject as Record<ValidFields, boolean>)[inputType] = isValid;
    }
  };

    const submitForm = async () => {
    const inputs = document.querySelectorAll<HTMLInputElement>(".dsa__reportJS");
    const isReportFormValid = checkForm(inputs, isFormValidObject, checkField);

    if (isReportFormValid) {
      updateFormData(formInputs);
      if (reportCommentID.value && service) {
      try {
        const key = `dsa_report`;
        const commentId = String(reportCommentID.value);
        localStorage.setItem(key, commentId);
      } catch (error) {
        console.warn('LocalStorage:', error);
      }
    }
      appStore.submitForm();
    } else {
      console.error("Form is not valid. Check require fields. DSA_Widget_FORM");
    }
  };

  const resetForm = () => {
    formInputs.reason = 'Wybierz powód zgłoszenia';
    formInputs.explain = '';
    formInputs.userEmail = '';
    formInputs.userName = '';
    formInputs.rules = false;
    selectValue.value = '';
    selectDataSet.value = '';
  };

  return {
    formInputs,
    selectDataSet,
    selectList,
    showExplainWarning,
    isHelperRequired,
    isMailAdded,
    helperTextArea,
    isExplainIncorrect,
    changeSelect,
    checkField,
    submitForm,
    resetForm
  };
}

export const checkForm = (inputs: NodeListOf<HTMLInputElement>, formObject: FormValidation, checkField: (input: any) => void) => {
  inputs.forEach((input) => checkField(input));   
  const isSomeError = hasAnyFalse(formObject);
  return !isSomeError;
};

export const checkTextArea = (input: any):string =>{
    const inputValueLength = input?.value ? input.value.length : 0;
    let helper = "";

    if (inputValueLength === 0) {
      helper = "";
    } else if (inputValueLength > 0 && inputValueLength < 10) {
      helper = "Minimalna liczba znaków wynosi 10.";
    } else if (inputValueLength >= 10 && inputValueLength <= 500) {
      helper = "Maksymalna liczba znaków wynosi 500."; 
    } else {
      helper = "Przekroczono maksymalną liczbę znaków (500).";
    }

    return helper;
};

export const isEmailValid = (email: string) => {
  const emailRegex = /^(|[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;
  return emailRegex.test(email);
};

export const hasAnyFalse = (obj: FormValidation) => {
  return Object.values(obj).some((value) => value === false);
};

export const prepareUrl = (location: Location, commentsID: string, serviceConfig: Form) => {
  const commentTarget = serviceConfig?.commentTarget; //"commentId" lub "opinion";
  const testingUrl = serviceConfig?.testingUrl;
  const url = location;
  const origin = url.origin;
  const pathname = url.pathname;
  const newUrl = origin + pathname + `?${testingUrl}#${commentTarget}${commentsID}`;
  
  return newUrl;
}